/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: David Helmick
 * @ Modified time: 2022-03-24 16:19:27
 * @ Description: Component for the user to upload files from their system to be used as a proforma
 */

import React from 'react'

import {
  Card,
  Button,
  List,
  ListItem,
  IconButton,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import { head } from '@/utils/lodash'

import { useDropzone } from 'react-dropzone'

import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone'
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone'
import CheckIcon from '@mui/icons-material/Check'
import { useMutation } from '@apollo/client/react'
import { UPDATE_PROPERTY } from '@/housefolios-components/PropertyContainer/mutations'
import { GET_PROPERTY } from '@/housefolios-components/PropertyContainer/queries'

import { GET_SIGNED_REQUEST } from '../../queries'
import axios from 'axios'
import { withUploadMember } from '@/utils/fileUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import withSession from '@/housefolios-components/Session/withSession'

function UploadProformaFileUploaderComponent(props) {
  const { proformaFile } = props
  const [updateProperty] = useMutation(UPDATE_PROPERTY)
  const [getSignedRequest] = useMutation(GET_SIGNED_REQUEST)

  const {
    isDragActive,
    isDragAccept,
    isDragReject,
    getRootProps,
    getInputProps,
  } = useDropzone({
    maxFiles: 1,
    accept: {
      'image/*': [],
      'application/pdf': [],
    },
    onDrop: async (acceptedFiles) => {
      props.setLoader(true)
      const file = head(acceptedFiles)
      const {
        data: { getSignedRequest: signed },
      } = await getSignedRequest({
        variables: {
          fileInput: withUploadMember(
            {
              filename: file.name,
              filetype: file.type,
              collection: 'proformas',
              documentId: props.propertyId,
            },
            props.session,
          ),
        },
      })
      const { signedRequest, url } = signed

      const options = {
        headers: {
          'Content-type': file.type,
        },
      }
      try {
        await axios.put(signedRequest, file, options)
        const { profile = {} } = props.session.me

        updateProperty({
          variables: {
            id: props.propertyId,
            propertyInput: {
              proformaFile: {
                filename: file.name,
                filesize: file.size,
                filetype: file.type,
                url: url,
                createdAt: new Date(),
                createdBy: `${profile.firstname} ${profile.lastname}`,
              },
            },
          },
          refetchQueries: [
            {
              query: GET_PROPERTY,
              variables: { id: props.propertyId },
            },
          ],
        }).finally(() => {
          props.setLoader(false)
        })
      } catch (error) {
        return error
      }
    },
  })

  const files = proformaFile
    ? [
        <ListItem key={proformaFile.filename} sx={[{ px: '1rem', py: '0.5rem', color: '#368cff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', }, { fontSize: "0.875rem !important" }]}>
          <span>{proformaFile.filename}</span>{' '}
          <Box
            component="span"
            data-hf="file-size"
            sx={{
              display: "inline-block",
              py: 0,
              px: "0.7em",
              fontSize: "70%",
              fontWeight: 700,
              lineHeight: "21px",
              textAlign: "center",
              whiteSpace: "nowrap",
              verticalAlign: "baseline",
              borderRadius: 0,
              textTransform: "uppercase",
              height: 20,
              pr: "0.85em",
              pl: "0.85em",
              backgroundColor: "rgb(100%, 98.5067873303%, 94.7058823529%) !important",
              color: "#f4772e",
            }}
          >
            {proformaFile.filesize} bytes
          </Box>
          <IconButton
            onClick={() => {
              props.setLoader(true)
              updateProperty({
                variables: {
                  id: props.propertyId,
                  propertyInput: {
                    proformaFile: {},
                  },
                },
                refetchQueries: [
                  {
                    query: GET_PROPERTY,
                    variables: { id: props.propertyId },
                  },
                ],
              }).finally(() => {
                props.setLoader(false)
              })
            }}
            size="large"
          >
            <FontAwesomeIcon icon={['far', 'circle-xmark']} sx={{ fontSize: "1rem !important" }} />
          </IconButton>
        </ListItem>,
      ]
    : []

  return (
    <>
      <Card sx={[{
        "@media (min-width: 1100px)": { p: "3rem" },
        mt: "1.5rem",
        p: "1rem",
      }, { boxShadow: "0 0.18rem 0.3rem rgba(166, 166, 185, 0.5), 0 0.326rem 3rem rgba(79, 80, 98, 0.3) !important" }]}>
        <Box data-hf="dropzone" sx={{
          "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
        }}>
          <Box
            {...getRootProps()} sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              outline: "none !important",
              "& *": { outline: "none !important" },
            }}
          >
            <input {...getInputProps()} />
            <Box data-hf="dropzone-inner" sx={{
              padding: "3rem 1rem",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              textAlign: "center",
              color: "#a6a6b9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              border: "2px dashed #e6e7f1",
              borderRadius: "0.65rem",
              backgroundColor: "#f8f9ff",
              "&:hover": { borderColor: "#368cff", color: "#4f5062" },
              "html[data-color-mode=\"dark\"] &": { backgroundColor: "#2a2a2a !important", borderColor: "rgba(255, 255, 255, 0.28) !important", color: "rgba(255, 255, 255, 0.7) !important" },
              "html[data-color-mode=\"dark\"] &:hover": { borderColor: "#368cff !important", color: "rgba(255, 255, 255, 0.87) !important", backgroundColor: "#333 !important" },
            }}>
              {isDragAccept && (
                <div>
                  <Box sx={{
                    height: "100px !important",
                    width: "100px !important",
                    lineHeight: "100px !important",
                    display: "inline-flex !important",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    transition: "all 0.2s ease-in-out !important",
                    transform: "scale(1) !important",
                    "&:hover": { transform: "scale(1.12)" },
                    mb: "1rem",
                    backgroundColor: "#1bc943",
                    borderRadius: "50%",
                    color: "#fff",
                    boxShadow: "0 0.46875rem 2.1875rem rgba(27, 201, 67, 0.03), 0 0.9375rem 1.40625rem rgba(27, 201, 67, 0.03), 0 0.25rem 0.53125rem rgba(27, 201, 67, 0.05), 0 0.125rem 0.1875rem rgba(27, 201, 67, 0.03) !important",
                  }}>
                    <CheckIcon  sx={{
                      height: "50px !important",
                      width: "50px !important",
                      lineHeight: "50px !important",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0,
                    }}/>
                  </Box>
                  <Box sx={[{ color: '#1bc943' }, { fontSize: "0.875rem !important" }]}>
                    All files will be uploaded!
                  </Box>
                </div>
              )}
              {isDragReject && (
                <div>
                  <Box sx={{
                    height: "100px !important",
                    width: "100px !important",
                    lineHeight: "100px !important",
                    display: "inline-flex !important",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    transition: "all 0.2s ease-in-out !important",
                    transform: "scale(1) !important",
                    "&:hover": { transform: "scale(1.12)" },
                    mb: "1rem",
                    backgroundColor: "#f83245",
                    borderRadius: "50%",
                    color: "#fff",
                    boxShadow: "0 0.46875rem 2.1875rem rgba(248, 50, 69, 0.03), 0 0.9375rem 1.40625rem rgba(248, 50, 69, 0.03), 0 0.25rem 0.53125rem rgba(248, 50, 69, 0.05), 0 0.125rem 0.1875rem rgba(248, 50, 69, 0.03) !important",
                  }}>
                    <CloseTwoToneIcon  sx={{
                      height: "50px !important",
                      width: "50px !important",
                      lineHeight: "50px !important",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0,
                    }}/>
                  </Box>
                  <Box sx={[{ color: '#f83245' }, { fontSize: "0.875rem !important" }]}>
                    Some files will be rejected!
                  </Box>
                </div>
              )}
              {!isDragActive && (
                <div>
                  <Box sx={{
                    height: "100px !important",
                    width: "100px !important",
                    lineHeight: "100px !important",
                    display: "inline-flex !important",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    border: 0,
                    transition: "all 0.2s ease-in-out !important",
                    transform: "scale(1) !important",
                    "&:hover": { transform: "scale(1.12)" },
                    mb: "1rem",
                    backgroundColor: "#fff",
                    borderRadius: "50%",
                    color: "#368cff",
                    boxShadow: "0 0.46875rem 2.1875rem rgba(244, 245, 253, 0.03), 0 0.9375rem 1.40625rem rgba(244, 245, 253, 0.03), 0 0.25rem 0.53125rem rgba(244, 245, 253, 0.05), 0 0.125rem 0.1875rem rgba(244, 245, 253, 0.03) !important",
                  }}>
                    <CloudUploadTwoToneIcon  sx={{
                      height: "50px !important",
                      width: "50px !important",
                      lineHeight: "50px !important",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0,
                    }}/>
                  </Box>
                  <Box sx={{ fontSize: "0.875rem !important" }}>
                    Drag and drop your pdf files here
                  </Box>
                </div>
              )}

              <Box
                component="small"
                data-hf="muted"
                sx={{
                  py: '0.5rem',
                  color: 'rgba(7, 9, 25, 0.5)',
                  'html[data-color-mode="dark"] &': {
                    color: 'rgba(255, 255, 255, 0.68) !important',
                  },
                }}
              >
                or
              </Box>
              <div>
                <Button
                 
                  sx={{
                    boxShadow: "0 0.25rem 0.55rem rgba(54, 140, 255, 0.35)",
                    color: "#fff",
                    backgroundColor: "#368cff",
                    backgroundImage: "none",
                    borderColor: "#368cff",
                    "&:not(:disabled):hover": { backgroundColor: "#368cff", color: "#fff" },
                    "&:not(:disabled).active, &:not(:disabled).active:hover, &:not(:disabled):active": { backgroundColor: "#036fff" },
                    "&:not(:disabled):active": { boxShadow: "0 0.22rem 0.525rem rgba(54, 140, 255, 0.4), 0 0.0625rem 0.385rem rgba(54, 140, 255, 0.54)" },
                    "&:not(:disabled).active .btn-wrapper--icon svg path, &:hover .btn-wrapper--icon svg path": { fill: "#fff" },
                    "html[data-color-mode=\"dark\"] &": { color: "#fff !important" },
                    transition: "all 0.2s ease-in-out !important",
                    transform: "scale(1) !important",
                    position: "relative",
                    zIndex: "0",
                    "&:hover": { transform: "scale(1.08)", zIndex: 3 },
                    borderRadius: "4.5rem",
                    fontWeight: 700,
                    px: "1.5rem",
                  }}
                >
                  <Box component="span" sx={{ px: '0.5rem' }}>
                    Browse Files
                  </Box>
                </Button>
              </div>
            </Box>
          </Box>
        </Box>
        <div>
          <Box sx={[{ fontWeight: 700, my: '1.5rem', textTransform: 'uppercase', color: '#4f5062', textAlign: 'center', }, { fontSize: "0.875rem !important" }]}>
            Uploaded File
          </Box>
          {files.length <= 0 && (
            <Box sx={[{ color: '#11c5db', textAlign: 'center' }, { fontSize: "0.875rem !important" }]}>
              Uploaded files will appear here!
            </Box>
          )}
          {files.length > 0 && (
            <div>
              {/* <Alert severity="success">
                You have uploaded <b>{files.length}</b> files!
              </Alert> */}
              <List component="div" sx={{ fontSize: "0.875rem !important" }}>
                {files}
              </List>
            </div>
          )}
        </div>
      </Card>
    </>
  )
}

export default withSession(UploadProformaFileUploaderComponent)
